import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { ExternalLink } from 'lucide-react';

export const Hero = ({ heroData, settings }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleShopAmazon = () => {
    if (heroData?.onRedeemClick) {
      heroData.onRedeemClick();
    } else {
      const url = heroData?.cta_url || settings?.amazon_shop_url || 'https://amazon.com';
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (!heroData) return null;

  return (
    <section 
      className="relative flex items-center overflow-hidden" 
      style={{ 
        minHeight: '600px',
        height: 'auto',
        backgroundImage: heroData.hero_background_image_url ? `url('${heroData.hero_background_image_url}')` : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="hero-container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" style={{ paddingTop: '6rem' }}>
        <div className="flex flex-col lg:grid lg:grid-cols-2 lg:gap-12 items-center">
          {/* Left Side - Product Image */}
          <div className={`relative transition-all duration-1000 lg:mb-0 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            {/* Product Image */}
            <div className="relative z-10 w-full mx-auto lg:ml-auto lg:mr-0" style={{ maxWidth: '28rem' }}>
              <div className="relative">
                <img 
                  src={heroData.product_box_image_url}
                  alt="Planeskeeper Card Sleeves Box"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          </div>

          {/* Right Side - Content */}
          <div className={`space-y-3 md:space-y-4 text-left transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`} style={{ marginTop: '0rem', marginBottom: '1rem' }}>
            {/* Badge on top - Updated colors and padding */}
            <div className="inline-flex items-center space-x-2 px-4 md:px-5 rounded-full" style={{ backgroundColor: 'rgb(255 107 0 / 18%)', paddingTop: '0.3rem', paddingBottom: '0.3rem' }}>
              <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: 'rgb(255, 62, 0)' }}></div>
              <span className="gaming-text font-bold uppercase tracking-widest text-xs md:text-sm" style={{ color: 'rgb(255, 84, 0)' }}>{heroData.badge_text}</span>
            </div>

            {/* Main Title - Left aligned on all screens */}
            <div className="space-y-1 md:space-y-2">
              <h2 className="gaming-title text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight text-left" style={{ marginTop: '0.5rem', fontSize: '2.8rem' }}>
                {heroData.title_line_1 && heroData.title_line_2 ? (
                  <>
                    <div className="text-white uppercase">{heroData.title_line_1}</div>
                    <div className="text-orange-500 uppercase" style={{ 
                      textShadow: '0 0 30px rgba(255, 74, 0, 0.5)'
                    }}>
                      {heroData.title_line_2}
                    </div>
                  </>
                ) : (
                  heroData.title.split('MULTIVERSE').map((part, index) => (
                    <React.Fragment key={index}>
                      {part}
                      {index < heroData.title.split('MULTIVERSE').length - 1 && (
                        <span 
                          style={{ 
                            color: '#FF4A00',
                            textShadow: '0 0 20px rgba(255, 74, 0, 0.6), 0 0 40px rgba(255, 74, 0, 0.4)'
                          }}
                        >
                          MULTIVERSE
                        </span>
                      )}
                    </React.Fragment>
                  ))
                )}
              </h2>
            </div>

            {/* Subtitle */}
            <h1 className="gaming-text text-base md:text-lg lg:text-xl font-medium text-white leading-relaxed hero-subtitle text-left" style={{ marginTop: '0.5rem' }}>
              {heroData.subtitle}
            </h1>

            {/* Description (optional - for relics page) */}
            {heroData.description && (
              <p className="text-gray-300 text-sm md:text-base lg:text-lg leading-relaxed text-left" style={{ marginBottom: '1rem', marginTop: '0' }}>
                {heroData.description}
              </p>
            )}

            {/* CTA Button */}
            <div style={{ marginTop: '0.5rem', marginBottom: '2rem' }}>
              {heroData.secondary_cta_text ? (
                <div className="flex flex-col lg:flex-row items-stretch lg:items-center space-y-3 lg:space-y-0 lg:space-x-4 max-w-md lg:max-w-none">
                  <Button
                    onClick={heroData.onRedeemClick || (() => window.location.href = heroData.cta_url || '#')}
                    size="lg"
                    className="gaming-text w-full lg:w-auto bg-gradient-to-r from-[#FF4A00] to-[#FF4A00] hover:from-orange-500 hover:to-orange-600 text-white font-bold uppercase tracking-wider shadow-2xl shadow-orange-600/50 transition-all duration-300 transform hover:scale-105 py-3 md:py-4"
                    style={{ fontSize: '14px' }}
                  >
                    {heroData.cta_text}
                  </Button>
                  <Button
                    onClick={() => window.open(heroData.secondary_cta_url || settings?.amazon_shop_url, '_blank', 'noopener,noreferrer')}
                    size="lg"
                    className="gaming-text w-full lg:w-auto bg-transparent border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white font-bold uppercase tracking-wider transition-all duration-300 py-3 md:py-4"
                    style={{ fontSize: '14px' }}
                  >
                    <ExternalLink className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                    {heroData.secondary_cta_text}
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={handleShopAmazon}
                  size="lg"
                  className="hero-cta gaming-text bg-gradient-to-r from-[#FF4A00] to-[#FF4A00] hover:from-orange-500 hover:to-orange-600 text-white font-bold uppercase tracking-wider shadow-2xl shadow-orange-600/50 transition-all duration-300 transform hover:scale-105"
                  style={{ fontSize: '18px', marginTop: '0.5rem' }}
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  {heroData.cta_text}
                </Button>
              )}
            </div>

            {/* Feature Badges - Only show if badges exist and not explicitly empty */}
            {(!heroData.badges || heroData.badges.length > 0) && heroData.badges && (
              <div className="flex flex-wrap gap-3 pt-2">
                {heroData.badges.map((badge, index) => (
                  <div key={index} className="flex items-center space-x-2 bg-transparent backdrop-blur-sm px-4 py-2 rounded border border-orange-500/50">
                    <div className="w-1 h-4 bg-gradient-to-b from-[#FF4A00] to-[#FF5A10]"></div>
                    <span className="gaming-text font-bold text-white uppercase tracking-wide" style={{ fontSize: '16px' }}>{badge}</span>
                  </div>
                ))}
              </div>
            )}
            {/* Default badges for main landing page only */}
            {!heroData.badges && !heroData.secondary_cta_text && (
              <div className="flex flex-wrap gap-3 pt-2">
                <div className="flex items-center space-x-2 bg-transparent backdrop-blur-sm px-4 py-2 rounded border border-orange-500/50">
                  <div className="w-1 h-4 bg-gradient-to-b from-[#FF4A00] to-[#FF5A10]"></div>
                  <span className="gaming-text font-bold text-white uppercase tracking-wide" style={{ fontSize: '16px' }}>Tournament Grade</span>
                </div>
                <div className="flex items-center space-x-2 bg-transparent backdrop-blur-sm px-4 py-2 rounded border border-orange-500/50">
                  <div className="w-1 h-4 bg-gradient-to-b from-[#FF4A00] to-[#FF5A10]"></div>
                  <span className="gaming-text font-bold text-white uppercase tracking-wide" style={{ fontSize: '16px' }}>Premium Quality</span>
                </div>
                <div className="flex items-center space-x-2 bg-transparent backdrop-blur-sm px-4 py-2 rounded border border-orange-500/50">
                  <div className="w-1 h-4 bg-gradient-to-b from-[#FF4A00] to-[#FF5A10]"></div>
                  <span className="gaming-text font-bold text-white uppercase tracking-wide" style={{ fontSize: '16px' }}>Ultra Clear</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
